import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

const PHOTO_BUCKET = 'exercise-photos'

function resolvePhotoUrl(path) {
  if (!path) return null
  if (path.startsWith('http')) return path
  const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path)
  return data?.publicUrl || null
}

// Fetches a single session with its nested exercise_logs (sorted by
// order_index), sets (sorted by set_number), and cardio_logs (sorted by
// order_index). Each log resolves its exercise's display fields.
export function useSessionDetail(id) {
  const { user } = useAuth()
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user || !id) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('sessions')
      .select(`
        *,
        exercise_logs(
          id, order_index, notes, exercise_id,
          exercise:exercises(name, muscle_group, equipment_type, machine_photo_url),
          sets(id, set_number, weight_kg, reps, rpe, is_warmup, is_failure)
        ),
        cardio_logs(*)
      `)
      .eq('id', id)
      .maybeSingle()
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    if (!data) {
      setSession(null)
      setLoading(false)
      return
    }
    const logs = [...(data.exercise_logs || [])]
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      .map((l) => ({
        ...l,
        exercise: l.exercise
          ? { ...l.exercise, photoUrl: resolvePhotoUrl(l.exercise.machine_photo_url) }
          : l.exercise,
        sets: [...(l.sets || [])].sort((a, b) => (a.set_number ?? 0) - (b.set_number ?? 0)),
      }))
    const cardios = [...(data.cardio_logs || [])].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    setSession({ ...data, exercise_logs: logs, cardio_logs: cardios })
    initialLoadedRef.current = true
    setLoading(false)
  }, [id, user])

  useEffect(() => { fetch() }, [fetch])

  const deleteSession = async () => {
    if (!id) return false
    const { error: err } = await supabase.from('sessions').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    return true
  }

  return { session, loading, error, refetch: fetch, deleteSession }
}
